import { createSelector } from '@reduxjs/toolkit';
import _ from 'lodash';
import { DateTime } from 'luxon';

// nasty require cycle
import { SortingOrders } from 'config/constants';
import { getEffectiveTabBar } from 'store/App/constants';
import AppSelectors from 'store/App/selectors';
import { ItemPreparationStatuses, OrderPreparationStatuses } from 'store/Orders/enums';
import { Order, OrderItem } from 'store/Orders/types';
import SettingSelectors from 'store/Settings/selectors';
import { Category } from 'store/Settings/types';

import { orderItemsAdapter, ordersAdapter } from './entities';
import {
  filterByConfiguredOrderKinds,
  filterByOrderKinds,
  filterDeliveryOrders,
} from './filterByOrderKinds';
import {
  countGroupedOrdersForTab,
  createTabBarOrderCountMap,
  groupOrderItemsByStatus,
  groupOrdersByInternalStatus,
} from './groupOrdersByTab';
import { getOrderInternalStatusWeight, getStartPreparationStatusWeight } from './utils';
import type { RootState } from 'store/types';

const orderEntitySelectors = ordersAdapter.getSelectors((state: RootState) => state.orders.list);
const orderItemEntitySelectors = orderItemsAdapter.getSelectors(
  (state: RootState) => state.orders.items,
);

const { selectAll, selectIds, selectById, selectEntities } = orderEntitySelectors;
const {
  selectAll: selectAllItems,
  selectEntities: selectItemEntities,
  selectById: selectItemById,
} = orderItemEntitySelectors;

const selectAllEntities = createSelector(selectAll, (orders) => _.keyBy(orders, 'uuid'));

const getItemsForOrder = (
  orders: Record<string, Order>,
  orderItems: Record<string, OrderItem>,
  orderId: string,
) => {
  const itemIds = orders[orderId]?.item_uuids ?? [];
  return itemIds
    .map((itemId) => orderItems[itemId])
    .filter((item): item is OrderItem => item !== undefined);
};

const filterItemsByCategories = (
  items: (OrderItem | undefined)[] = [],
  selectedCategories: Category[] = [],
  isSelectedAllCategories: boolean,
) => {
  const existingItems = items.filter((item): item is OrderItem => item !== undefined);

  return isSelectedAllCategories
    ? existingItems
    : existingItems.filter((item) => selectedCategories.includes(item.category));
};

const itemsForOrderSelector = createSelector(
  [selectEntities, selectItemEntities],
  (orders, orderItems) => {
    return (orderId: string) => {
      return getItemsForOrder(orders, orderItems, orderId);
    };
  },
);

const selectItemsForOrder = createSelector(
  [itemsForOrderSelector, (state, orderId) => orderId],
  (selectItems, orderId) => selectItems(orderId),
);

const itemsForOrderWithCategoriesSelector = createSelector(
  [
    SettingSelectors.selectSelectedCategories,
    SettingSelectors.selectIsSelectedAllCategories,
    itemsForOrderSelector,
  ],
  (selectedCategories, isSelectedAllCategories, selectItems) => {
    return (orderId: string) => {
      const items = selectItems(orderId);

      return filterItemsByCategories(items, selectedCategories, isSelectedAllCategories);
    };
  },
);

const selectItemsForOrderWithCategories = createSelector(
  [itemsForOrderWithCategoriesSelector, (state, orderId) => orderId],
  (selectItems, orderId) => {
    return selectItems(orderId);
  },
);

const selectWithCategories = createSelector(
  [selectAll, selectItemEntities],
  (orders, itemsById) => {
    return orders.map((order) => ({
      ...order,
      categories: _.uniq(order.item_uuids.map((itemUUID) => itemsById[itemUUID]?.category)),
    }));
  },
);

const selectForCategories = createSelector(
  [
    selectWithCategories,
    SettingSelectors.selectSelectedCategories,
    SettingSelectors.selectIsSelectedAllCategories,
  ],
  (orders, selectedCategories, isSelectedAllCategories) =>
    isSelectedAllCategories
      ? orders
      : orders.filter((order) => _.intersection(order.categories, selectedCategories).length > 0),
);

const selectDeliveryOrdersForCategories = createSelector(selectForCategories, filterDeliveryOrders);

const selectOrdersForEffectiveOrderKinds = createSelector(
  [
    selectForCategories,
    SettingSelectors.selectSelectedOrderKinds,
    SettingSelectors.selectIsSelectedAllOrderKinds,
    (state: RootState) => state.orders.view.isDeliveryOnlyEnabled,
  ],
  filterByOrderKinds,
);

/**
 * Product exception: sidebar tab counts must not react to the transient
 * Delivery Only shortcut. This population still honors Categories and the
 * configured Order Types. Rendered lists must use
 * selectOrdersForEffectiveOrderKinds instead.
 */
const selectOrdersForSidebarTabCounts = createSelector(
  [
    selectForCategories,
    SettingSelectors.selectSelectedOrderKinds,
    SettingSelectors.selectIsSelectedAllOrderKinds,
  ],
  filterByConfiguredOrderKinds,
);

const selectIsDeliveryOnlyEnabled = createSelector(
  (state: RootState) => state.orders.view,
  (view) => view.isDeliveryOnlyEnabled,
);

const selectAllSorted = createSelector(
  [
    selectOrdersForEffectiveOrderKinds,
    SettingSelectors.selectOrdersSortingOrder,
    itemsForOrderWithCategoriesSelector,
  ],
  (orders, sortingOrder, selectItems) => {
    switch (sortingOrder) {
      case SortingOrders.BY_SCHEDULED_FOR_ASC: {
        return _.sortBy(orders, 'scheduled_for');
      }
      case SortingOrders.BY_PROMISED_TIME_AT_ASC: {
        return _.sortBy(orders, 'promised_time_at');
      }
      case SortingOrders.BY_START_PREPARATION_STATUS_DESC: {
        return _.sortBy<Order>(orders, [
          (order) => getStartPreparationStatusWeight(order.start_preparation_status),
          (order) =>
            getOrderInternalStatusWeight({
              order: order,
              items: selectItems(order.uuid),
            }),
          'latest_prep_time',
        ]);
      }
      case SortingOrders.BY_PLACED_AT_ASC: {
        return _.sortBy(orders, 'created_at');
      }
      default: {
        return orders;
      }
    }
  },
);

const selectGrouped = createSelector(
  [selectAllSorted, itemsForOrderWithCategoriesSelector],
  groupOrdersByInternalStatus,
);

const selectSidebarTabCountOrdersGrouped = createSelector(
  [selectOrdersForSidebarTabCounts, itemsForOrderWithCategoriesSelector],
  groupOrdersByInternalStatus,
);

const selectSidebarTabCountOrderItemsGrouped = createSelector(
  [selectOrdersForSidebarTabCounts, itemsForOrderWithCategoriesSelector],
  groupOrderItemsByStatus,
);

/**
 * These navigation summaries intentionally ignore only Delivery Only. While
 * that shortcut is active they are not the lengths of the rendered lists.
 */
const selectSidebarTabOrderCountsByScreen = createSelector(
  [selectSidebarTabCountOrdersGrouped, selectSidebarTabCountOrderItemsGrouped],
  (overviewOrders, chefOrders) => ({
    overview: createTabBarOrderCountMap(overviewOrders),
    chef: createTabBarOrderCountMap(chefOrders),
  }),
);

const selectDeliveryOrdersGrouped = createSelector(
  [selectDeliveryOrdersForCategories, itemsForOrderWithCategoriesSelector],
  groupOrdersByInternalStatus,
);

const selectDeliveryOrderItemsGrouped = createSelector(
  [selectDeliveryOrdersForCategories, itemsForOrderWithCategoriesSelector],
  groupOrderItemsByStatus,
);

const selectDeliveryOrderCountByScreen = createSelector(
  [
    selectDeliveryOrdersGrouped,
    selectDeliveryOrderItemsGrouped,
    AppSelectors.selectCurrentTabBar,
    SettingSelectors.selectCardViewLayoutStatusTab,
  ],
  (overviewOrders, chefOrders, selectedTab, cardViewOrganisation) => ({
    overview: countGroupedOrdersForTab(
      overviewOrders,
      getEffectiveTabBar(selectedTab, 'overview', cardViewOrganisation),
    ),
    chef: countGroupedOrdersForTab(
      chefOrders,
      getEffectiveTabBar(selectedTab, 'chef', cardViewOrganisation),
    ),
  }),
);

const selectOrderCount = createSelector(selectAllSorted, (all) => all.length);
const selectNotBeingPrepared = createSelector(selectGrouped, (grouped) => grouped.notBeingPrepared);
const selectNotBeingPreparedCount = createSelector(
  selectNotBeingPrepared,
  (notBeingPrepared) => notBeingPrepared.length,
);

const selectBeingPrepared = createSelector(selectGrouped, (grouped) => grouped.beingPrepared);
const selectBeingPreparedCount = createSelector(
  selectBeingPrepared,
  (beingPrepared) => beingPrepared.length,
);

const selectPrepared = createSelector(selectGrouped, (grouped) => grouped.prepared);
const selectPreparedCount = createSelector(selectPrepared, (prepared) => prepared.length);

const selectScheduled = createSelector(selectGrouped, (grouped) => grouped.scheduled);
const selectScheduledCount = createSelector(selectScheduled, (scheduled) => scheduled.length);

const selectScheduledGrouped = createSelector(selectScheduled, (orders) => {
  const todayKey = DateTime.now().toLocaleString(DateTime.DATE_SHORT);
  const grouped = _.groupBy(orders, (order) =>
    DateTime.fromISO(order.scheduled_for).toLocaleString(DateTime.DATE_SHORT),
  );

  let today = [];
  const rest = [];

  for (const key in grouped) {
    if (key === todayKey) {
      today = grouped[key];
    } else {
      rest.push({
        date: grouped[key][0].scheduled_for,
        items: grouped[key],
      });
    }
  }

  return [today, _.sortBy(rest, 'date')];
});

const selectItemsGroupedAsOrder = createSelector(
  [selectAllSorted, itemsForOrderSelector],
  groupOrderItemsByStatus,
);

const selectFilteredItemsGroupedAsOrder = createSelector(
  selectItemsGroupedAsOrder,
  selectItemEntities,
  SettingSelectors.selectSelectedCategories,
  SettingSelectors.selectIsSelectedAllCategories,
  (grouped, itemsById, selectedCategories, isSelectedAllCategories) => {
    return Object.fromEntries(
      Object.entries(grouped).map(([key, orders]) => {
        if (!orders || !Array.isArray(orders)) {
          return [key, orders];
        }

        const newOrders = orders.map((order: Order) => {
          const items = filterItemsByCategories(
            order.item_uuids.map((uuid) => itemsById[uuid]),
            selectedCategories,
            isSelectedAllCategories,
          ).filter((item) => !!item);

          return {
            ...order,
            filtered_items_uuids: items.map(({ uuid }) => uuid),
          };
        });

        return [key, newOrders];
      }),
    );
  },
);

const selectNotBeingPreparedItemsAsOrder = createSelector(
  selectFilteredItemsGroupedAsOrder,
  (grouped) => grouped.notBeingPrepared,
);

const selectPreparedItemsAsOrder = createSelector(
  selectFilteredItemsGroupedAsOrder,
  (grouped) => grouped.prepared,
);

const selectBeingPreparedItemsAsOrder = createSelector(
  selectFilteredItemsGroupedAsOrder,
  (grouped) => grouped.beingPrepared,
);
const selectScheduledItemsAsOrder = createSelector(
  selectFilteredItemsGroupedAsOrder,
  (grouped) => grouped.scheduled,
);

const selectNotBeingPreparedItemsAsOrderFiltered = createSelector(
  selectNotBeingPreparedItemsAsOrder,
  (orders: Order[]) =>
    orders.filter((order) => order.filtered_items_uuids && order.filtered_items_uuids.length > 0),
);

const selectPreparedItemsAsOrderFiltered = createSelector(
  selectPreparedItemsAsOrder,
  (orders: Order[]) =>
    orders.filter((order) => order.filtered_items_uuids && order.filtered_items_uuids.length > 0),
);

const selectBeingPreparedItemsAsOrderFiltered = createSelector(
  selectBeingPreparedItemsAsOrder,
  (orders: Order[]) =>
    orders.filter((order) => order.filtered_items_uuids && order.filtered_items_uuids.length > 0),
);

const selectNewAndCookingItemsAsOrderFiltered = createSelector(
  [selectBeingPreparedItemsAsOrderFiltered, selectNotBeingPreparedItemsAsOrderFiltered],
  (beingPrepared, notBeingPrepared) => [...beingPrepared, ...notBeingPrepared],
);

const selectScheduledItemsAsOrderFiltered = createSelector(
  selectScheduledItemsAsOrder,
  (orders: Order[]) =>
    orders.filter((order) => order.filtered_items_uuids && order.filtered_items_uuids.length > 0),
);

const selectScheduledItemsCount = createSelector(
  selectScheduledItemsAsOrderFiltered,
  selectItemEntities,
  (orders: Order[], itemById) => {
    return orders.reduce((acc, order) => {
      const items = order.filtered_items_uuids?.map((uuid) => itemById[uuid]);

      return acc + _.sumBy(items, 'quantity');
    }, 0);
  },
);

const selectNotPreparedItemsCount = createSelector(
  selectNotBeingPreparedItemsAsOrderFiltered,
  selectItemEntities,
  (orders: Order[], itemById) => {
    return orders.reduce((acc, order) => {
      const items = order.filtered_items_uuids?.map((uuid) => itemById[uuid]);

      return acc + _.sumBy(items, 'quantity');
    }, 0);
  },
);

const selectBeingPreparedItemsCount = createSelector(
  selectBeingPreparedItemsAsOrderFiltered,
  selectItemEntities,
  (orders: Order[], itemById) => {
    return orders.reduce((acc, order) => {
      const items = order.filtered_items_uuids?.map((uuid) => itemById[uuid]);

      return acc + _.sumBy(items, 'quantity');
    }, 0);
  },
);

const selectPreparedItemsCount = createSelector(
  selectPreparedItemsAsOrderFiltered,
  selectItemEntities,
  (orders: Order[], itemById) => {
    return orders.reduce((acc, order) => {
      const items = order.filtered_items_uuids?.map((uuid) => itemById[uuid]);

      return acc + _.sumBy(items, 'quantity');
    }, 0);
  },
);

const selectItemsCountByCategory = createSelector(
  [selectAll, selectItemEntities],
  (activeOrders, itemsById) => {
    const itemUUIDs = activeOrders.reduce(
      (result, order) =>
        [
          OrderPreparationStatuses.NOT_BEING_PREPARED,
          OrderPreparationStatuses.BEING_PREPARED,
        ].includes(order.status)
          ? [...result, ...order.item_uuids]
          : result,
      [],
    );

    const itemCountByCategories = itemUUIDs.reduce((result, uuid) => {
      const item = itemsById[uuid];

      const previousValue = result[item.category] ?? 0;

      return item?.status === ItemPreparationStatuses.NOT_BEING_PREPARED
        ? { ...result, [item.category]: previousValue + item.quantity }
        : result;
    }, {});

    return itemCountByCategories;
  },
);

const selectItemsCountByOrderKind = createSelector(
  [selectAll, selectItemEntities, selectEntities],
  (activeOrders, itemsById, ordersById) => {
    const itemUUIDs = activeOrders.reduce(
      (result, order) =>
        [
          OrderPreparationStatuses.NOT_BEING_PREPARED,
          OrderPreparationStatuses.BEING_PREPARED,
        ].includes(order.status)
          ? [...result, ...order.item_uuids]
          : result,
      [],
    );

    const itemCountByCategories = itemUUIDs.reduce((result, uuid) => {
      const item = itemsById[uuid];
      const order = ordersById[item.order_uuid];

      const previousValue = result[order.kind] ?? 0;

      return item?.status === ItemPreparationStatuses.NOT_BEING_PREPARED
        ? { ...result, [order.kind]: previousValue + item.quantity }
        : result;
    }, {});

    return itemCountByCategories;
  },
);

const selectItemsByIds = createSelector(
  [selectItemEntities, (state, itemUUIDs: string[]) => itemUUIDs],
  (itemsById, itemUUIDs) => {
    return itemUUIDs.map((uuid) => itemsById[uuid]).filter((item) => !!item);
  },
);

export default {
  selectIds,
  selectById,
  selectPrepared,
  selectItemById,
  selectScheduled,
  selectAllSorted,
  selectItemsForOrder,
  selectForCategories,
  selectItemsForOrderWithCategories,
  selectPreparedItemsAsOrder,
  selectPreparedItemsAsOrderFiltered,
  selectBeingPrepared,
  selectPreparedCount,
  selectScheduledCount,
  selectScheduledGrouped,
  selectNotBeingPrepared,
  selectBeingPreparedCount,
  selectBeingPreparedItemsAsOrder,
  selectBeingPreparedItemsAsOrderFiltered,
  selectPreparedItemsCount,
  selectScheduledItemsCount,
  selectItemsCountByCategory,
  selectAllEntities,
  selectNotPreparedItemsCount,
  selectNotBeingPreparedItemsAsOrder,
  selectNotBeingPreparedItemsAsOrderFiltered,
  selectNotBeingPreparedCount,
  selectBeingPreparedItemsCount,
  selectItemsCountByOrderKind,
  selectSidebarTabOrderCountsByScreen,
  selectDeliveryOrderCountByScreen,
  selectIsDeliveryOnlyEnabled,
  selectOrderCount,
  selectItemsGroupedAsOrder,
  selectScheduledItemsAsOrder,
  selectScheduledItemsAsOrderFiltered,
  selectItemsByIds,
  selectNewAndCookingItemsAsOrderFiltered,
};
